/** Tiny DOM helpers so the UI modules stay readable. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function button(
  label: string,
  onClick: () => void,
  className = 'button',
): HTMLButtonElement {
  const node = el('button', className, label);
  node.type = 'button';
  node.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return node;
}

export interface SliderOptions {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  format?: (value: number) => string;
  onChange: (value: number) => void;
}

export function slider(options: SliderOptions): HTMLElement {
  const wrapper = el('div', 'setting');
  const label = el('label', 'setting-label');
  const value = el('span', 'setting-value');
  const input = el('input', 'setting-input');
  input.type = 'range';
  input.min = String(options.min);
  input.max = String(options.max);
  input.step = String(options.step);
  input.value = String(options.value);

  const format = options.format ?? ((v: number) => v.toFixed(2));
  const render = (v: number): void => {
    value.textContent = format(v);
  };
  render(options.value);

  input.addEventListener('input', () => {
    const parsed = Number(input.value);
    render(parsed);
    options.onChange(parsed);
  });

  label.append(document.createTextNode(options.label), value);
  wrapper.append(label, input);
  return wrapper;
}

export interface ToggleOptions {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function toggle(options: ToggleOptions): HTMLElement {
  const wrapper = el('div', 'setting setting-inline');
  const label = el('label', 'setting-label', options.label);
  const input = el('input', 'setting-toggle');
  input.type = 'checkbox';
  input.checked = options.value;
  input.addEventListener('change', () => options.onChange(input.checked));
  wrapper.append(label, input);
  return wrapper;
}

export interface SelectOptions<T extends string> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}

/** A labelled drop-down, for settings with a few discrete choices. */
export function select<T extends string>(options: SelectOptions<T>): HTMLElement {
  const wrapper = el('div', 'setting setting-inline');
  const label = el('label', 'setting-label', options.label);
  const input = el('select', 'setting-select');
  for (const option of options.options) {
    const node = el('option', undefined, option.label);
    node.value = option.value;
    input.append(node);
  }
  input.value = options.value;
  input.addEventListener('change', () => options.onChange(input.value as T));
  wrapper.append(label, input);
  return wrapper;
}
